import React, { useState } from 'react';
import { 
  Container, Paper, Typography, Button, Box, Grid, Avatar, Chip,
  CardActionArea
} from '@mui/material';
import { 
  ArrowForward, ArrowBack, Public, HomeOutlined, BusinessCenter,
  AccountBalanceWallet, TrendingUp, Domain, ShowChart, FlashOn,
  CheckCircle, VerifiedUser
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';
import ServiceContactForm from '../components/ServiceContactForm';

const itrServicesList = [
  { id: 'all', name: 'All ITR Forms', icon: <FlashOn /> },
  { id: 'itr1', name: 'ITR-1 (Salaried)', popular: true, formKey: 'ITR-1' },
  { id: 'itr2', name: 'ITR-2 (Capital Gains / NRI)', popular: true, formKey: 'ITR-2' },
  { id: 'itr3', name: 'ITR-3 (Business & F&O)', formKey: 'ITR-3' },
  { id: 'itr4', name: 'ITR-4 (Presumptive 44AD/ADA)', formKey: 'ITR-4' },
  { id: 'itru', name: 'ITR-U (Updated Past Returns)', formKey: 'ITR-U' },
  { id: 'notice', name: 'Tax Notice Response (143(1)/139(9))', formKey: 'ITR-Notice' }
];

interface Option { label: string; value: string; subtext?: string; icon: React.ReactNode; }
interface Question { id: number; text: string; description: string; options: Option[]; }

const ItrCalculator: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [result, setResult] = useState<string | null>(null);

  const questions: Question[] = [
    { id: 1, text: "Did you earn income in India or outside?", description: "Select your residency and source status for the financial year.", options: [{ label: "India Only", value: "india", subtext: "Income earned purely within India", icon: <Domain sx={{ fontSize: 36 }} /> }, { label: "Both (India & Outside)", value: "both", subtext: "Global income tracking required", icon: <Public sx={{ fontSize: 36 }} /> }, { label: "NRI Status", value: "nri", subtext: "Non-Resident Indian taxation rules", icon: <Public sx={{ fontSize: 36 }} /> }] },
    { id: 2, text: "Do you have a Salary Income?", description: "Did you stick to one employer or switch your jobs during the Financial Year?", options: [{ label: "From one employer", value: "single_salary", subtext: "Standard single Form 16", icon: <AccountBalanceWallet sx={{ fontSize: 36 }} /> }, { label: "From multiple employers", value: "multi_salary", subtext: "Multiple Form 16s received", icon: <AccountBalanceWallet sx={{ fontSize: 36 }} /> }, { label: "Not Applicable", value: "no_salary", subtext: "No salary/pension revenue streams", icon: <Typography sx={{ fontWeight: 800, fontSize: '20px' }}>N/A</Typography> }] },
    { id: 3, text: "Do you have a Business/Professional Income?", description: "Are you a small business owner, independent freelancer, or certified digital professional?", options: [{ label: "Small Business (< ₹3 Cr)", value: "presumptive_biz", subtext: "Eligible for Sec 44AD", icon: <BusinessCenter sx={{ fontSize: 36 }} /> }, { label: "Freelancer (< ₹75 Lakhs)", value: "presumptive_prof", subtext: "Eligible for Sec 44ADA", icon: <BusinessCenter sx={{ fontSize: 36 }} /> }, { label: "Full Books / Audit Accounts", value: "regular_biz", subtext: "Full audit tracks required", icon: <BusinessCenter sx={{ fontSize: 36 }} /> }, { label: "Not Applicable", value: "no_biz", subtext: "No traditional business activities", icon: <Typography sx={{ fontWeight: 800, fontSize: '20px' }}>N/A</Typography> }] },
    { id: 4, text: "Do you participate in Intraday Trading or F&O?", description: "Intraday equity trading and F&O derivatives are classified as Business Income by the Income Tax Department.", options: [{ label: "Yes, Intraday Stock", value: "intraday", subtext: "Speculative transactions", icon: <ShowChart sx={{ fontSize: 36 }} /> }, { label: "Yes, F&O Derivatives", value: "f_and_o", subtext: "Non-speculative transactions", icon: <ShowChart sx={{ fontSize: 36 }} /> }, { label: "No, Delivery-only / None", value: "no_trading", subtext: "Only long term investment or no stock activity", icon: <Typography sx={{ fontWeight: 800, fontSize: '20px' }}>N/A</Typography> }] },
    { id: 5, text: "Do you own a residential house property?", description: "Tell us about your homeownership stakes to calculate property deductions accurately.", options: [{ label: "Own One House", value: "single_house", subtext: "Self-occupied or single rental unit", icon: <HomeOutlined sx={{ fontSize: 36 }} /> }, { label: "Own Multiple Houses", value: "multi_house", subtext: "More than one declared property stake", icon: <HomeOutlined sx={{ fontSize: 36 }} /> }, { label: "No Home Stakes", value: "no_house", subtext: "Currently renting or living elsewhere", icon: <Typography sx={{ fontWeight: 800, fontSize: '20px' }}>N/A</Typography> }] },
    { id: 6, text: "Do you have any Income from Capital Gains?", description: "Did you sell house property, land, or delivery-based mutual fund units/shares during the year?", options: [{ label: "Sold Property / Land", value: "cg_prop", subtext: "Real estate sale transactions", icon: <TrendingUp sx={{ fontSize: 36 }} /> }, { label: "Sold Delivery / MFs", value: "cg_market", subtext: "Long term / short term equity sale", icon: <TrendingUp sx={{ fontSize: 36 }} /> }, { label: "Not Applicable", value: "no_cg", subtext: "No investment asset transfers", icon: <Typography sx={{ fontWeight: 800, fontSize: '20px' }}>N/A</Typography> }] },
    { id: 7, text: "Is your Income above ₹50 Lakhs or do you hold unlisted equity?", description: "High-value thresholds trigger different regulatory filing criteria in India.", options: [{ label: "Yes, High Value", value: "yes_high", subtext: "Income > ₹50L or own unlisted shares/directorships", icon: <Typography sx={{ fontWeight: 800, fontSize: '18px' }}>YES</Typography> }, { label: "No Standard", value: "no_high", subtext: "Income up to ₹50L and standard retail holdings", icon: <Typography sx={{ fontWeight: 800, fontSize: '18px' }}>NO</Typography> }] }
  ];

  const handleOptionSelect = (value: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentStep].id]: value }));
  };

  const getFormKey = (): string => {
    if (activeFilter === 'itr1') return 'ITR-1';
    if (activeFilter === 'itr2') return 'ITR-2';
    if (activeFilter === 'itr3') return 'ITR-3';
    if (activeFilter === 'itr4') return 'ITR-4';
    if (activeFilter === 'itru') return 'ITR-U';
    if (activeFilter === 'notice') return 'ITR-Notice';

    const q1 = answers[1];
    const q2 = answers[2];
    const q3 = answers[3];
    const q4 = answers[4];
    const q5 = answers[5];
    const q6 = answers[6];
    const q7 = answers[7];

    if (q3 === 'regular_biz' || q4 === 'intraday' || q4 === 'f_and_o') {
      return 'ITR-3';
    }
    if (q3 === 'presumptive_biz' || q3 === 'presumptive_prof') {
      return 'ITR-4';
    }
    if (q1 === 'both' || q1 === 'nri' || q2 === 'multi_salary' || q5 === 'multi_house' || q6 !== 'no_cg' || q7 === 'yes_high') {
      return 'ITR-2';
    }
    return 'ITR-1';
  };

  const computeResultText = () => {
    const formKey = getFormKey();
    let resultText = '';
    if (formKey === 'ITR-1') resultText = 'ITR-1 (Sahaj - Salaried Individuals with Single House Property)';
    else if (formKey === 'ITR-2') resultText = 'ITR-2 (Capital Gains, Multiple Properties & NRI Income)';
    else if (formKey === 'ITR-3') resultText = 'ITR-3 (Business Turnover, F&O Trading & Audit Accounts)';
    else if (formKey === 'ITR-4') resultText = 'ITR-4 (Sugam - Presumptive Business/Profession under Sec 44AD/ADA)';
    else if (formKey === 'ITR-U') resultText = 'ITR-U (Updated Past Returns for Missed/Revised Income)';
    else resultText = 'ITR Notice & Scrutiny Response (Section 143(1) / 139(9))';
    setResult(resultText);
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      computeResultText();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
    setActiveFilter('all');
  };

  const activeQuestion = questions[currentStep];
  const selectedValue = answers[activeQuestion.id] || '';

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', py: 6 }}>
      <SEOHead
        title="ITR Filing Wizard — Find Your ITR Form | Fingard Partners"
        description="Use our smart ITR wizard to determine which Income Tax Return form you need. Get expert CA-assisted filing with transparent consultation."
        keywords="ITR Calculator, Income Tax Return Filing, ITR-1, ITR-2, ITR-3, ITR-4, File ITR Online India"
        canonicalUrl="/itr-calculator"
      />
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Chip label="Income Tax & ITR Filing Portal" color="primary" sx={{ mb: 1.5, fontWeight: 'bold', bgcolor: '#123D6B' }} />
          <Typography variant="h4" fontWeight={800} color="#0B2847" gutterBottom>
            Income Tax Return (ITR) Diagnostic Wizard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
            Select your specific ITR Form or answer our diagnostic questions to find your exact tax filing schedule.
          </Typography>
        </Box>

        {/* QUICK-NAVIGATION FILTER CHIPS */}
        <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {itrServicesList.map((serv) => {
              const isSelected = activeFilter === serv.id;
              return (
                <Chip
                  key={serv.id}
                  label={serv.name}
                  onClick={() => {
                    setActiveFilter(serv.id);
                    if (serv.id !== 'all') {
                      setResult(serv.formKey + ' (' + serv.name + ')');
                    } else {
                      setResult(null);
                    }
                  }}
                  color={isSelected ? "primary" : "default"}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{
                    py: 2,
                    px: 1,
                    fontWeight: isSelected ? 800 : 500,
                    fontSize: '0.85rem',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    borderColor: isSelected ? '#123D6B' : '#E2E8F0',
                    bgcolor: isSelected ? '#123D6B' : '#FFFFFF',
                    color: isSelected ? '#FFFFFF' : '#334155',
                    '&:hover': { bgcolor: isSelected ? '#123D6B' : '#F1F5F9' }
                  }}
                />
              );
            })}
          </Box>
        </Paper>

        <Container maxWidth="md">
          {!result ? (
            <Paper elevation={0} sx={{ p: { xs: 4, md: 5 }, borderRadius: '24px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.03)' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: '0.05em', color: '#C59B27', mb: 1.5, textAlign: 'center' }}>
                  QUESTION {currentStep + 1} OF {questions.length}
                </Typography>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 1, mb: 5 }}>
                  {questions.map((_, index) => (
                    <Box key={index} sx={{ width: index === currentStep ? 24 : 8, height: 8, borderRadius: '4px', bgcolor: index === currentStep ? '#123D6B' : index < currentStep ? '#C59B27' : '#E2E8F0', transition: 'all 0.3s ease' }} />
                  ))}
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#123D6B', letterSpacing: '-0.02em' }}>{activeQuestion.text}</Typography>
                  <Typography variant="body1" sx={{ color: '#64748B', mb: 4, fontSize: '15px' }}>{activeQuestion.description}</Typography>
                  
                  <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
                    {activeQuestion.options.map((option) => {
                      const isSelected = selectedValue === option.value;
                      return (
                        <Grid item xs={12} sm={4} key={option.value}>
                          <CardActionArea onClick={() => handleOptionSelect(option.value)} sx={{ p: 3, height: '100%', borderRadius: '16px', border: '2px solid', borderColor: isSelected ? '#123D6B' : '#E2E8F0', bgcolor: isSelected ? 'rgba(11, 40, 71, 0.02)' : 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'all 0.2s', '&:hover': { borderColor: isSelected ? '#123D6B' : '#C59B27' } }}>
                            <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: isSelected ? '#123D6B' : '#F1F5F9', color: isSelected ? '#FFFFFF' : '#475569', transition: 'all 0.2s' }}>{option.icon}</Avatar>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#123D6B', fontSize: '15px' }}>{option.label}</Typography>
                            {option.subtext && <Typography variant="caption" sx={{ color: '#64748B', display: 'block', px: 1 }} align="center">{option.subtext}</Typography>}
                          </CardActionArea>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 3, borderTop: '1px solid #E2E8F0' }}>
                    <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} disabled={currentStep === 0} sx={{ borderRadius: '8px', textTransform: 'none', px: 3, fontWeight: 600, color: '#475569', borderColor: '#CBD5E1' }}>Previous</Button>
                    <Button variant="contained" disableElevation endIcon={<ArrowForward />} onClick={handleNext} disabled={!selectedValue} sx={{ bgcolor: '#123D6B', color: '#FFFFFF', borderRadius: '8px', textTransform: 'none', px: 4, fontWeight: 600, '&:hover': { bgcolor: '#C59B27' } }}>Next</Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              {/* Diagnostic Recommendation Card */}
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <VerifiedUser sx={{ color: '#10B981', fontSize: 32 }} />
                  <Box>
                    <Typography variant="overline" color="text.secondary" fontWeight={700}>
                      DIAGNOSTIC RECOMMENDATION
                    </Typography>
                    <Typography variant="h5" fontWeight={800} color="#0B2847">
                      {result}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="#475569" sx={{ mb: 2 }}>
                  Based on your diagnostic profile, our Chartered Accountants will verify your AIS/TIS, prepare accurate depreciation & capital gains calculations, and maximize eligible deductions under Section 80C to 80U.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip icon={<CheckCircle sx={{ fontSize: '16px !important' }} />} label="AIS / 26AS Cross-Verification" size="small" sx={{ bgcolor: '#F1F5F9', color: '#334155', fontWeight: 600 }} />
                  <Chip icon={<CheckCircle sx={{ fontSize: '16px !important' }} />} label="Zero Defect Guarantee" size="small" sx={{ bgcolor: '#F1F5F9', color: '#334155', fontWeight: 600 }} />
                  <Chip icon={<CheckCircle sx={{ fontSize: '16px !important' }} />} label="Dedicated CA Support" size="small" sx={{ bgcolor: '#F1F5F9', color: '#334155', fontWeight: 600 }} />
                </Box>
              </Paper>

              {/* ServiceContactForm Component */}
              <ServiceContactForm
                serviceCategory="income-tax"
                selectedService={result}
                diagnosticAnswers={answers}
                onBackToQuiz={handleReset}
              />
            </Box>
          )}
        </Container>
      </Container>
    </Box>
  );
};

export default ItrCalculator;
